import React, {useContext, useEffect, useRef, useState} from 'react';
import {
    Animated,
    Dimensions,
    FlatList,
    Modal,
    Platform,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View,
} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import LottieView from "lottie-react-native";
import emptyCartNotification from "../../assets/animations/empty_cart.json";
import {LinearGradient} from "expo-linear-gradient";
import {CartContext} from "../helpers/CartContext";
import {priceFormatter} from "../helpers/numbers";
import {calcDeliveryFee} from "../helpers/deliveryHelper";
import {OnlineOrderContext} from '../helpers/OnlineOrderContext';
//import PaymentModal from './PaymentModal';
import {Gesture, GestureDetector, GestureHandlerRootView, Swipeable} from 'react-native-gesture-handler';
import DiscountModal from "../components/DiscountModal";
import CommentModal from "../components/CommentModal";
import HappyAlert from "../helpers/crossPlatformTools/HappyAlert";
import {runOnJS} from "react-native-reanimated/src";
import {TableContext} from '../helpers/TableContext';
import AsyncStorage from "@react-native-async-storage/async-storage";
import {send_storeOrder} from "../helpers/paymentHelper";
import {OrdersContext} from "../helpers/OrdersContext";
import PaymentSelector from '../components/PaymentSelector';
import {SystemContext} from "../helpers/SystemContext"; // Adjust the path as necessary
import {useTranslation} from 'react-i18next';
import '../../i18n';
import {RawBTPrintKitchen} from "../helpers/rawBTDriver";
import {VipCardContext} from "../helpers/VipCardContext";
import {TourContext} from "../helpers/TourContext";
import CustomTooltip from "../helpers/CustomTooltip";
import Spinner from "react-native-loading-spinner-overlay";
import {useTheme} from '../helpers/ThemeContext';
import {useThemedStyles} from '../helpers/useThemedStyles';
import {usePosLayout} from '../helpers/posLayout';
import {haptics} from '../helpers/haptics';
import PosNumpadSheet from '../components/pos/PosNumpadSheet';

const { width, height } = Dimensions.get('window');
const AUTHORIZED_TO_MODIFY = true;

const normalizeId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
        return value.$oid || value._id || value.id || '';
    }
    return '';
};

const CartScreen = ({ navigation }) => {
    const { t, i18n } = useTranslation();
    const currentLocale = i18n.language || 'en';
    const { theme, isDark } = useTheme();
    const styles = useThemedStyles(buildStyles);
    const { isPhone: isPhoneLayout, isPhoneUi } = usePosLayout();
    // Phone UI: quantity is edited on an on-screen numpad sheet instead of the OS keyboard.
    const [numpadItem, setNumpadItem] = useState(null);
    const { cartItems, addToCart, cartTotal, removeFromCart, setCart, deleteCart, addComment, productsPrice, discountsForWholeCart, setDiscountsForWholeCart, serviceFeeAmount, cartTotalWithServiceFee, alreadySentCartItems, setAlreadySentCartItems, paymentModalVisible, setPaymentModalVisible } = React.useContext(CartContext);
    const [extraIsExpanded, setExtraIsExpanded] = useState(null);
    const { fetchSystemData } = useContext(SystemContext);
    const [selectedRow, setSelectedRow] = useState(null);
    const opacity = useRef(new Animated.Value(1)).current;
    const [isCartExpanded, setIsCartExpanded] = useState(false);
    const [commentModalVisible, setCommentModalVisible] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [commentSuccess, setCommentSuccess] = useState(null);
    const [discountModalVisible, setDiscountModalVisible] = useState(false);
    const [selectedItemPrice, setSelectedItemPrice] = useState(null);
    const [openFrom, setOpenFrom] = useState(null);
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
    const { currentTable, removeTable } = React.useContext(TableContext);
    const { fetchOrdersFromAPI, orders, upsertOrder } = React.useContext(OrdersContext);
    const { currentStep, endTour, nextStep } = useContext(TourContext);
    const [serviceFee, setServiceFee] = useState(0);
    const [deliveryFee, setDeliveryFee] = useState(0);
    const [isPaymentChoseVisible, setIsPaymentChoseVisible] = useState(false);
    const { orderData, resetOnlineOrder } = React.useContext(OnlineOrderContext);
    const [editingItemId, setEditingItemId] = useState(null);
    const [inputQuantity, setInputQuantity] = useState('');
    const [combinedCartItems, setCombinedCartItems] = useState([]);
    const { selectedVipCard, removeVipCard, selectedEmployee, removeEmployee } = useContext(VipCardContext);
    const [isSendingDisabled, setIsSendingDisabled] = useState(false);
    const isSendingOrderRef = useRef(false);
    const isQuickReceiptMode = !currentTable && !orderData?.orderType;

    useEffect(() => {
        setCombinedCartItems([
            ...cartItems.map(item => ({ ...item, canBeModified: true })).reverse(),
            ...alreadySentCartItems.map(item => ({ ...item, canBeModified: AUTHORIZED_TO_MODIFY, hasBackground: true })),
        ]);
    }, [alreadySentCartItems, cartItems]);

    useEffect(() => {
        //Table cleanup if the table is closed but not cleaned up from cart
        if (currentTable) {
            const currentTableId = normalizeId(currentTable?._id);
            const tableOrder = orders.find(
                (order) =>
                    !order.storno &&
                    order.status !== "cancelled" &&
                    order.status !== "closed" &&
                    normalizeId(order?.tableId) === currentTableId
            );
            if (!tableOrder && alreadySentCartItems?.length > 0) {
                removeTable()
                setAlreadySentCartItems([]);
                setDiscountsForWholeCart({ amount: 0, percentage: 0 })
                resetOnlineOrder();
            }
        }
    }, [orders]);

    const handleDeleteCart = () => {
        haptics.heavy();
        deleteCart();
        setIsConfirmModalVisible(false);
    };

    async function handleOrderSend() {
        // Synchronous re-entry guard: `isSendingDisabled` only flips after a
        // re-render, so two fast taps could both pass and send the order twice.
        if (isSendingOrderRef.current) {
            return;
        }
        isSendingOrderRef.current = true;
        let tableOrder;
        if (currentTable) {
            // Normalize BOTH sides of the tableId comparison. A server order's
            // tableId can arrive as an ObjectId-shaped object (or a differently
            // typed string) than currentTable._id; a strict === would then miss
            // the existing open order, so send_storeOrder would mint a brand-new
            // id and the backend would spawn a DUPLICATE order on the table
            // (added items land on the copy, the original tab keeps only its old
            // items). normalizeId collapses both to a plain id string.
            const currentTableId = normalizeId(currentTable._id);
            tableOrder = orders.find(
                (order) =>
                    !order.storno &&
                    order.status !== "cancelled" &&
                    order.status !== "closed" &&
                    normalizeId(order.tableId) === currentTableId
            );
        }
        try {
            setIsSendingDisabled(true);
            const response = await send_storeOrder(cartTotal, cartItems, "", 0, discountsForWholeCart, undefined, false,
                currentTable, serviceFeeAmount, true, alreadySentCartItems, tableOrder?._id, [],
                0, currentTable && serviceFeeAmount > 0 ? cartTotalWithServiceFee : cartTotal, (selectedVipCard ? selectedVipCard : selectedEmployee), {}, [], tableOrder, false, (deliveryFee || 0));
            if (response) {
                if (cartItems.length > 0) {
                    // Fire-and-forget: the print queue runs off the tap path, so
                    // the kitchen ticket starts immediately without blocking navigation.
                    const kitchenItems = cartItems.map(cI => ({ ...cI, round: 0 }));
                    RawBTPrintKitchen({ ...response, items: kitchenItems }, kitchenItems)
                        ?.catch?.((error) => console.warn("Kitchen printing failed:", error));
                }
                // Optimistically reflect the new order in shared state RIGHT NOW
                // so the floor-plan shows the table as occupied before navigation,
                // closing the window where a second order could be opened for it.
                upsertOrder?.(response);

                // Reconcile with the backend in the background — do NOT block the
                // return to the table map on this network round-trip.
                fetchOrdersFromAPI().catch((error) => {
                    console.warn("Failed to refresh orders after saving table order:", error);
                });

                removeTable();
                deleteCart()
                setAlreadySentCartItems([]);
                removeVipCard();
                removeEmployee();
                navigation.navigate("TablesScreen", { screen: "TableMap" });
            }
        } catch (error) {
            console.error("Order sending failed:", error);
        } finally {
            // Always release the button/spinner, even when no response came back.
            isSendingOrderRef.current = false;
            setIsSendingDisabled(false);
        }
    }

    const handleDiscountPress = (itemPrice) => {
        setSelectedItemPrice(itemPrice);
        setDiscountModalVisible(true);
    };

    useEffect(() => {
        if (cartItems?.length === 0 && alreadySentCartItems?.length === 0) {
            setIsCartExpanded(false)
        }
    }, [cartItems, alreadySentCartItems]);

    useEffect(() => {
        const fetchServiceFee = async () => {
            try {
                const restaurantPlace = await AsyncStorage.getItem('restaurantPlace');
                if (restaurantPlace) {
                    const serviceFee = JSON.parse(restaurantPlace).serviceFee;
                    setServiceFee(serviceFee);
                }
            } catch (error) {
                console.error('Failed to fetch service fee:', error);
            }
        };

        fetchServiceFee();
    }, []);

    useEffect(() => {
        const calculateDeliveryFee = async () => {
            try {
                if (orderData?.orderType === "online") {
                    const restaurantPlace = await AsyncStorage.getItem('restaurantPlace');
                    const restaurant = await AsyncStorage.getItem('restaurant');
                    if (restaurantPlace) {
                        const parsedRestaurantPlace = JSON.parse(restaurantPlace);
                        const parsedRestaurant = JSON.parse(restaurant);
                        const zipCode = orderData?.addressInfo?.zipCode || orderData?.addressInfo?.zip || orderData.zip || orderData.addressInfo.postal_code || orderData.addressInfo.postalcode || "";
                        const deliveryFeeAmount = calcDeliveryFee(zipCode, parsedRestaurant);
                        setDeliveryFee(deliveryFeeAmount);
                    }
                } else {
                    setDeliveryFee(0);
                }
            } catch (error) {
                console.error('Failed to calculate delivery fee:', error);
                setDeliveryFee(0);
            }
        };
        calculateDeliveryFee();
        // Only when the order data changes: without deps this ran after EVERY
        // render and re-read/parsed two storage documents each time.
    }, [orderData]);

    const incrementQuantity = (id) => {
        haptics.tap();
        setCart(cartItems.map(item => {
            if (item._id === id) {
                item.quantity += 1;
                const fullPrice = item.price * item.quantity;
                if (item.discounts) {
                    const discount = item.discounts;
                    if (discount.amount) {
                        item.remotePrice = fullPrice - discount.amount;
                    } else {
                        item.remotePrice = fullPrice - (fullPrice * (discount.percentage / 100));
                    }
                }
            }
            return item;
        }));
        if (alreadySentCartItems) {
            const updatedItems = alreadySentCartItems.map(item => {
                if (item._id === id) {
                    item.amount += 1;
                    const fullPrice = item.price * item.amount;
                    if (item.discounts) {
                        const discount = item.discounts;
                        if (discount.amount) {
                            item.remotePrice = fullPrice - discount.amount;
                        } else {
                            item.remotePrice = fullPrice - (fullPrice * (discount.percentage / 100));
                        }
                    }
                }
                return item;
            });
            setAlreadySentCartItems(updatedItems)
        }
    };

    const decrementQuantity = (id, count = 1) => {
        // Removing the last piece deletes the row: warn, otherwise a plain tick.
        const removing = cartItems.some(item => item._id === id && item.quantity <= count);
        if (removing) {
            haptics.warning();
        } else {
            haptics.tap();
        }
        setCart(cartItems.map(item => {
            if (item._id === id && item.quantity > count) {
                item.quantity -= count;
                const fullPrice = item.price * item.quantity;
                if (item.discounts) {
                    const discount = item.discounts;
                    if (discount.amount) {
                        item.remotePrice = fullPrice - discount.amount;
                    } else {
                        item.remotePrice = fullPrice - (fullPrice * (discount.percentage / 100));
                    }
                }
            } else if (item._id === id && item.quantity <= count) {
                return null; // Remove item if quantity is less than or equal to count
            }
            return item;
        }).filter(item => item !== null));
        if (alreadySentCartItems) {
            const updatedItems = alreadySentCartItems.map(item => {
                if (item._id === id && item.amount > count) {
                    item.amount -= 1;
                    const fullPrice = item.price * item.amount;
                    if (item.discounts) {
                        const discount = item.discounts;
                        if (discount.amount) {
                            item.remotePrice = fullPrice - discount.amount;
                        } else {
                            item.remotePrice = fullPrice - (fullPrice * (discount.percentage / 100));
                        }
                    }
                } else if (item._id === id && item.quantity <= count) {
                    return null; // Remove item if quantity is less than or equal to count
                }
                return item;
            });
            setAlreadySentCartItems(updatedItems)
        }
    };

    const handleAddComment = (itemId, commentText) => {
        addComment(itemId, commentText);
        setCommentModalVisible(false);
        setCommentText("");
    };

    const calculateTotal = () => {
        const alreadyInCartPrice = alreadySentCartItems?.length > 0
            ? alreadySentCartItems.reduce((total, item) => total + (item.remotePrice ? item.remotePrice : (item.price * (item.amount || 1))), 0)
            : 0;

        const cartItemsPrice = cartItems.reduce((total, item) => total + item.price * item.quantity, 0);

        return cartItemsPrice + alreadyInCartPrice;
    };
    const renderRightActions = (id) => (
        <Animated.View style={[styles.swipeButton, { opacity }]}>
            <Icon name="plus" size={isPhoneUi ? 32 : 20} color={isPhoneUi ? '#28a745' : theme.colors.text} />
        </Animated.View>
    );

    async function handleOrderSubmission(paymentType) {
        // Same double-tap guard as handleOrderSend: one submission at a time.
        if (isSendingOrderRef.current) {
            return;
        }
        isSendingOrderRef.current = true;
        const userInfo = {
            name: {
                last: orderData?.lastName || "",
                first: orderData?.firstName || "",
            },
            phone: orderData?.phoneNumber || "",
        };

        const addressInfo = {
            zip: orderData?.zip || "",
            city: orderData?.city || "",
            address: orderData?.street || "",
            deliveryInstructions: orderData?.note || "",
        };
        try {
            const response = await send_storeOrder(cartTotal, cartItems, paymentType, cartTotal, discountsForWholeCart, undefined, false, currentTable, serviceFeeAmount, false, alreadySentCartItems, undefined, [], 0, cartTotal, undefined, { ...orderData, userInfo: userInfo, addressInfo: orderData?.orderType === "online" ? addressInfo : "" });
            if (response) {
                deleteCart();
                resetOnlineOrder();
                // Background refreshes — never block the UI on these round-trips.
                Promise.resolve(fetchOrdersFromAPI?.()).catch((error) => console.warn("Failed to refresh orders:", error));
                Promise.resolve(fetchSystemData?.()).catch((error) => console.warn("Failed to refresh system data:", error));
                navigation.navigate('OnlineScreen');
            } else {
                console.error("Order sending failed:", response);
            }
            setIsPaymentChoseVisible(false);
        } catch (error) {
            console.error("Order sending failed:", error);
        } finally {
            isSendingOrderRef.current = false;
        }
    }

    const renderLeftActions = (id) => (
        <Animated.View style={[styles.swipeButton, { opacity }]}>
            <Icon name="minus" size={isPhoneUi ? 32 : 20} color={isPhoneUi ? '#E53935' : theme.colors.text} />
        </Animated.View>
    );

    function handleSwipe(direction, id) {
        // incrementQuantity / decrementQuantity fire their own haptic.
        direction === 'left' ? incrementQuantity(id) : decrementQuantity(id);
    }

    function swipeClose(swipeable, duration) {
        Animated.timing(opacity, {
            toValue: 0,
            duration: duration || 50,
            useNativeDriver: true,
        }).start(() => {
            swipeable.reset();
            opacity.setValue(1); // Reset opacity for next use
        });
    }

    const handleQuantityPress = (item) => {
        if (isPhoneUi) {
            haptics.select();
            setNumpadItem(item);

            return;
        }
        setEditingItemId(item._id);
        setInputQuantity(item?.quantity?.toString() || item?.amount?.toString());
    };

    /** Sets an absolute quantity (> 0) on the cart row and the already-sent mirror row. */
    const applyQuantity = (id, newQuantity) => {
        if (!Number.isFinite(newQuantity) || newQuantity <= 0) {
            return;
        }
        setCart(cartItems.map(item => {
            if (item._id === id) {
                item.quantity = newQuantity;
            }
            return item;
        }));
        if (alreadySentCartItems) {
            const updatedItems = alreadySentCartItems.map(item => {
                if (item._id === id) {
                    item.amount = newQuantity;
                }
                return item;
            });
            setAlreadySentCartItems(updatedItems);
        }
    };

    const handleQuantityConfirm = (id) => {
        applyQuantity(id, parseInt(inputQuantity, 10));
        setEditingItemId(null);
    };

    const handleQuantityCancel = () => {
        setEditingItemId(null);
    };

    const renderItem = ({ item, canBeModified, hasBackground }) => {
        const longPressGesture = Gesture.LongPress().onStart(() => {
            if (canBeModified) {
                runOnJS(setExtraIsExpanded)(item._id); // Safely call the state updater
                runOnJS(setIsCartExpanded)(false);    // Safely call other state updaters
            }
        })
            .onEnd(() => {
                // Prevent further handling of the gesture
                return true;
            });

        function getSelectedSize(item) {
            const sizeIndex = item.sizeIndex || 0
            return item.sizes && item.sizes[sizeIndex]
        }

        return (
            <GestureDetector gesture={longPressGesture}>
                <CustomTooltip
                    isVisible={currentStep === 4}
                    onNext={() => {
                        nextStep();
                    }}
                    placement="bottom"
                    module={"default"}
                    onClose={endTour}
                    wrapped={(<Swipeable
                        onSwipeableWillOpen={(direction) => handleSwipe(direction, item._id)}
                        onSwipeableOpen={(direction, swipeable) => swipeClose(swipeable)}
                        renderRightActions={() => canBeModified && renderLeftActions(item._id)}
                        renderLeftActions={() => canBeModified && renderRightActions(item._id)}
                    >
                        <View style={[styles.itemContainer, extraIsExpanded === item._id && styles.selectedItemContainer, { backgroundColor: hasBackground ? theme.colors.border : 'transparent', borderBottomWidth: item?.extras?.length > 0 ? 0 : 1, paddingBottom: item?.extras?.length > 0 ? 0 : 16 }]}>
                            <View style={{ flex: 1, paddingRight: 6 }}>
                                <Text style={styles.itemName} numberOfLines={2}>{item.name[currentLocale] || item.name.hu || item.name}</Text>
                                <Text style={styles.unitPrice}>{getSelectedSize(item)?.amount || "1"} {getSelectedSize(item)?.unit || "db"} / {priceFormatter(item.price)}</Text>
                                {item?.comments?.length > 0 && <Text style={styles.smallComment}>"{item?.comments?.join('", "')}"</Text>}
                            </View>
                            {canBeModified && <View style={[styles.quantityContainer, { flexShrink: 0, marginHorizontal: 4, justifyContent: 'center', alignItems: 'center' }]}>
                                {canBeModified && <TouchableOpacity style={[styles.quantityButton, isPhoneUi && styles.quantityButtonPhone]}
                                    accessibilityRole="button"
                                    hitSlop={{ top: 6, bottom: 6, left: 4, right: 4 }}
                                    onPress={() => decrementQuantity(item._id)}>
                                    <Icon name="minus" size={18} color={theme.colors.text} />
                                </TouchableOpacity>}
                                {editingItemId === item._id ? (
                                    <TextInput
                                        style={styles.quantityInput}
                                        value={inputQuantity}
                                        onChangeText={(text) => setInputQuantity(text.replace(/[^0-9]/g, ''))}
                                        keyboardType="numeric"
                                        autoFocus
                                        onBlur={() => handleQuantityCancel()}
                                        onSubmitEditing={() => handleQuantityConfirm(item._id)}
                                    />
                                ) : (
                                    <TouchableOpacity onPress={() => handleQuantityPress(item)} style={[styles.quantityValue, isPhoneUi && styles.quantityValuePhone]}>
                                        <Text style={[styles.quantityText, isPhoneUi && styles.quantityTextPhone]}>{item.quantity || item.amount}</Text>
                                    </TouchableOpacity>
                                )}
                                {canBeModified &&
                                    <CustomTooltip
                                        isVisible={currentStep === 5}
                                        onNext={() => {
                                            incrementQuantity(item._id)
                                            nextStep();
                                        }}
                                        placement="bottom"
                                        module={"default"}
                                        onClose={endTour}
                                        wrapped={(<TouchableOpacity style={[styles.quantityButton, isPhoneUi && styles.quantityButtonPhone]}
                                            accessibilityRole="button"
                                            hitSlop={{ top: 6, bottom: 6, left: 4, right: 4 }}
                                            onPress={() => incrementQuantity(item._id)}>
                                            <Icon name="plus" size={18} color={theme.colors.text} />
                                        </TouchableOpacity>
                                        )}
                                    />

                                }
                            </View>}
                            {!item.remotePrice || item?.remotePrice === item.price ? (<Text numberOfLines={1} style={[styles.itemPrice, { flexShrink: 0, minWidth: 64, marginLeft: 4, textAlign: 'right' }]}>{priceFormatter(Number(item.price) * (Number(item.quantity) || Number(item.amount)))}</Text>
                            ) : (<View style={{ marginLeft: 8 }}>
                                <Text style={[styles.itemName, { flex: 2, alignItems: "flex-end", textAlign: 'right', textDecorationLine: "line-through" }]}>
                                    {priceFormatter(Number(item.price) * (Number(item.quantity) || Number(item.amount)))}
                                </Text>
                                <Text style={styles.unitPrice}>{priceFormatter(item.remotePrice)}</Text></View>)}
                        </View>
                        {item?.extras?.length > 0 && (
                            <View style={{
                                flexDirection: "column", borderBottomWidth: 2,
                                borderBottomColor: theme.colors.border, paddingBottom: 16
                            }}>{item.extras.map((extra, index) => (
                                <View style={{ flex: 1, flexDirection: 'row', justifyContent: 'space-between' }}>
                                    <Text key={index}
                                        style={styles.smallComment}>+ {extra?.item?.name?.[currentLocale] || extra?.item?.name?.hu || extra?.item?.name}</Text>

                                    <Text key={index}
                                        style={styles.smallComment}> {priceFormatter(extra.price || extra?.item?.sizes[0]?.price)}</Text>
                                </View>))}
                            </View>)


                        }
                        {extraIsExpanded === item._id && (
                            <Animated.View style={[styles.extraRow, extraIsExpanded === item._id && styles.selectedExtraRow]}>
                                <TouchableOpacity style={[styles.chevronButton]} onPress={() => setExtraIsExpanded(false)}>
                                    <Icon name="chevron-up" size={25} color={theme.colors.text} />
                                </TouchableOpacity>
                                <TouchableOpacity style={[styles.extraButton, styles.deleteButton]} onPress={() => removeFromCart(item._id)}>
                                    <Icon name="trash-2" color="white" size={26} />
                                </TouchableOpacity>
                                <TouchableOpacity style={[styles.extraButton, styles.discountButton]} onPress={() => {
                                    setOpenFrom("cartItem");
                                    handleDiscountPress(item.price * item.quantity, item.remotePrice);
                                }}>
                                    <Icon name="percent" color="white" size={26} />
                                </TouchableOpacity>
                                <TouchableOpacity style={[styles.extraButton, styles.commentButton]} onPress={() => {
                                    setCommentModalVisible(true);
                                    setSelectedRow(item);
                                }}>
                                    <Icon name="message-square" color="white" size={26} />
                                </TouchableOpacity>
                            </Animated.View>
                        )}
                    </Swipeable>
                    )}
                />

            </GestureDetector>
        );
    };

    const handlePaymentPress = () => {
        //console.log(cartItems.length === 0 && alreadySentCartItems?.length === 0)
        //Online order
        if (cartItems.length === 0 && alreadySentCartItems?.length === 0) return;
        if (orderData?.orderType && orderData?.orderType?.length > 0 && !currentTable) {
            console.log(currentTable)
            console.log(orderData)
            console.log(orderData.orderType)
            setIsPaymentChoseVisible(true);
        } else {
            navigation.navigate("PaymentScreen");
            // if (width >= 768) {
            //     setPaymentModalVisible(true);
            // } else {
            //     navigation.navigate("PaymentScreen");
            // }
        }
    };

    return (
        <GestureHandlerRootView style={{ flex: 1 }}>

            <LinearGradient
                colors={isDark ? [theme.colors.background, theme.colors.card, theme.colors.background] : ['#EAE5EE', '#FEE6DE', '#EBE5F2']}
                style={{ flex: 1, width: '100%', height: '100%' }}
            >
                <Spinner
                    visible={isSendingDisabled}
                    textContent={t("processing...")}
                    textStyle={styles.spinnerTextStyle}
                />
                <View style={styles.container}>
                    {currentTable && (
                        <TouchableOpacity
                            style={styles.tableNameContainer}
                            onPress={() => {
                                removeTable();
                                navigation.navigate("TablesScreen");
                            }}
                        >
                            <Text style={styles.tableNameText}>{t("table")}: {currentTable.name}</Text>
                        </TouchableOpacity>
                    )}
                    {isQuickReceiptMode && (
                        <View style={styles.quickReceiptContainer}>
                            <Text style={styles.quickReceiptText}>{t('quick_receipt')}</Text>
                        </View>
                    )}
                    {(cartItems.length > 0 || alreadySentCartItems?.length > 0) && <>{(alreadySentCartItems?.length > 0 && combinedCartItems?.length > 0) ? <FlatList
                        data={combinedCartItems}
                        style={{ flex: 1 }}
                        keyboardShouldPersistTaps="handled"
                        keyboardDismissMode="on-drag"
                        renderItem={({ item }) => renderItem({ item, canBeModified: item.canBeModified, hasBackground: item.hasBackground })}
                        keyExtractor={(item, index) => `${item.id}-${index}`}
                        initialNumToRender={10}
                        maxToRenderPerBatch={5}
                        windowSize={5}
                        removeClippedSubviews={Platform.OS === 'android'}
                    /> :
                        cartItems.length > 0 && <FlatList
                            data={[...cartItems].reverse()}
                            keyboardShouldPersistTaps="handled"
                            keyboardDismissMode="on-drag"
                            renderItem={({ item }) => renderItem({ item, canBeModified: true })}
                            keyExtractor={(item, index) => `${item.id}-${index}`}
                            initialNumToRender={10}
                            maxToRenderPerBatch={5}
                            windowSize={5}
                            removeClippedSubviews={Platform.OS === 'android'}
                        />}</>}
                    {cartItems.length === 0 && alreadySentCartItems?.length === 0 && <View style={{
                        flex: 1,
                        alignContent: 'center',
                        alignItems: 'center'
                    }}>
                        <View style={{
                            flex: 1,
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 120
                        }}>
                            <LottieView
                                source={emptyCartNotification}
                                autoPlay
                                // Play once: an endlessly looping idle animation (visible most of
                            // the day on the tablet cart panel) costs frames on low-end devices.
                            loop={false}
                                style={styles.lottie}
                                renderMode={'AUTOMATIC'}
                                resizeMode={'center'}
                                enableSafeModeAndroid={true}
                            />
                            <Text style={{ fontSize: 22, color: theme.colors.text, marginTop: 20 }}>
                                {t("cart_empty")}
                            </Text>
                        </View>
                    </View>}
                    {isCartExpanded && (
                        <View style={[styles.totalContainer, styles.brighterBackground]}>
                            <CustomTooltip
                                isVisible={currentStep === 7}
                                onNext={() => {
                                    nextStep();
                                }}
                                placement="top"
                                module={"default"}
                                onClose={endTour}
                                wrapped={(<View style={[styles.brighterBackground, { flexDirection: 'row', alignItems: 'center' }]}>
                                    <TouchableOpacity onPress={() => setIsConfirmModalVisible(true)} style={[styles.extraButton, styles.deleteButton]}>
                                        <Icon name="trash-2" color="#ccc" size={20} />
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        style={[styles.extraButton, styles.discountButton]}
                                        onPress={() => {
                                            setOpenFrom("cartTotal");
                                            setSelectedItemPrice(undefined);
                                            setDiscountModalVisible(true);
                                        }}
                                    >
                                        <Icon name="percent" color="#eee" size={20} />
                                    </TouchableOpacity>
                                </View>
                                )}
                            />

                        </View>
                    )}
                    {(cartItems.length > 0 || alreadySentCartItems?.length > 0) && (
                        <CustomTooltip
                            isVisible={currentStep === 6}
                            onNext={() => {
                                setIsCartExpanded(true)
                                nextStep();
                            }}
                            placement="top"
                            module={"default"}
                            onClose={endTour}
                            wrapped={(<TouchableOpacity
                                onPress={() => {
                                    if (calculateTotal() > 0) {
                                        setExtraIsExpanded(null);
                                        setIsCartExpanded(!isCartExpanded);
                                    }
                                }}
                            >
                                <View style={[styles.detailsContainer, isCartExpanded && styles.brighterBackground, { flexDirection: 'row', alignItems: 'center', borderTopWidth: 1, borderTopColor: theme.colors.border }]}>
                                    <Text style={styles.productSumText}> {t("products_total")}</Text>
                                    <Text style={styles.productSumText}>{priceFormatter(productsPrice)} </Text>
                                </View>

                                {discountsForWholeCart && (discountsForWholeCart?.amount > 0 || discountsForWholeCart?.percentage > 0) && (
                                    <View style={[styles.detailsContainer, isCartExpanded && styles.brighterBackground, { flexDirection: 'row', alignItems: 'center' }]}>
                                        <Text style={styles.detailsText}>
                                            {discountsForWholeCart.amount > 0 ? ` -${priceFormatter(discountsForWholeCart.amount)} :` : ` (${discountsForWholeCart.percentage}%):`}
                                        </Text>
                                        <Text style={styles.detailsText}>
                                            {discountsForWholeCart.amount > 0
                                                ? ` -${priceFormatter(discountsForWholeCart.amount)}`
                                                : ` -${priceFormatter(productsPrice * (discountsForWholeCart?.percentage / 100))} `}
                                        </Text>
                                    </View>
                                )}

                                {currentTable && (
                                    <View style={[styles.detailsContainer, isCartExpanded && styles.brighterBackground, { flexDirection: 'row', alignItems: 'center' }]}>
                                        <Text style={styles.detailsText}>{t("service_fee")}({serviceFee}%):</Text>
                                        <Text style={styles.detailsText}>{serviceFeeAmount.toFixed(1).replace(/\.0$/, '')} Ft </Text>
                                    </View>
                                )}

                                {isCartExpanded && orderData?.orderType === "online" && deliveryFee > 0 && (
                                    <View style={[styles.detailsContainer, styles.brighterBackground, { flexDirection: 'row', alignItems: 'center' }]}>
                                        <Text style={styles.detailsText}>{t("delivery_fee")}:</Text>
                                        <Text style={styles.detailsText}>{priceFormatter(deliveryFee)} </Text>
                                    </View>
                                )}

                                <View style={[styles.totalContainer, isCartExpanded && styles.brighterBackground, { flexDirection: 'row', alignItems: 'center' }]}>
                                    <Text style={styles.totalText}>{t("payable")}:</Text>
                                    <Icon name={isCartExpanded ? "chevron-down" : "chevron-up"} color={theme.colors.text} size={20} />
                                    <Text style={styles.totalText}>{priceFormatter(
                                        (currentTable && serviceFeeAmount > 0 ? cartTotalWithServiceFee : cartTotal) +
                                        (orderData?.orderType === "online" ? deliveryFee : 0)
                                    )}</Text>
                                </View>
                            </TouchableOpacity>
                            )}
                        />

                    )}
                    {(cartItems?.length > 0 || alreadySentCartItems?.length > 0) && (
                        <CustomTooltip
                            isVisible={currentStep === 8}
                            onNext={() => {
                                nextStep();
                            }}
                            placement="top"
                            module={"default"}
                            onClose={endTour}
                            wrapped={(
                                <View style={[styles.totalContainer, { fontSize: 40, padding: 5, flexDirection: 'row', justifyContent: 'space-between' }]}>
                                    <TouchableOpacity
                                        style={[!currentTable ? styles.processButton : styles.saveTableButton, isPhoneUi && styles.processButtonPhone, { flex: 1, marginRight: 1 }]}
                                        disabled={cartItems?.length === 0 && alreadySentCartItems?.length === 0}
                                        onPress={() => {
                                            haptics.tap();
                                            handlePaymentPress();
                                        }}
                                    >
                                        <Text style={[!currentTable ? styles.processButtonText : styles.saveTableText, isPhoneUi && styles.processButtonTextPhone]}>{t("payment")}</Text>
                                    </TouchableOpacity>
                                    {currentTable && (
                                        <TouchableOpacity style={[styles.processButton, isPhoneUi && styles.processButtonPhone, { flex: 1, marginLeft: 1 }]} disabled={isSendingDisabled} onPress={() => {
                                            haptics.tap();
                                            handleOrderSend();
                                        }}>
                                            <Text style={[styles.processButtonText, isPhoneUi && styles.processButtonTextPhone]}>{t("send")}</Text>
                                        </TouchableOpacity>
                                    )}
                                </View>
                            )}
                        />
                    )}
                    {isPhoneUi && (
                        <PosNumpadSheet
                            visible={!!numpadItem}
                            onClose={() => setNumpadItem(null)}
                            onConfirm={(value) => {
                                applyQuantity(numpadItem?._id, Math.floor(value));
                                setNumpadItem(null);
                            }}
                            title={numpadItem ? (numpadItem?.name?.[currentLocale] || numpadItem?.name?.hu || String(numpadItem?.name || '')) : ''}
                            initialValue={numpadItem ? (numpadItem.quantity || numpadItem.amount || '') : ''}
                            maxLength={4}
                            validate={(n) => n >= 1}
                        />
                    )}
                    {discountModalVisible && <DiscountModal
                        visible={discountModalVisible}
                        onClose={() => setDiscountModalVisible(false)}
                        totalPrice={calculateTotal()}
                        itemPrice={selectedItemPrice}
                        selectedCartItemID={extraIsExpanded}
                        openFrom={openFrom}
                    />}
                    {commentModalVisible && <CommentModal
                        visible={commentModalVisible}
                        onClose={() => setCommentModalVisible(false)}
                        itemName={selectedRow?.name[currentLocale] || selectedRow?.name.hu || selectedRow?.name}
                        commentText={commentText}
                        setCommentText={setCommentText}
                        handleAddComment={handleAddComment}
                        itemId={selectedRow?._id}
                        commentSuccess={commentSuccess}
                    />}
                    {isConfirmModalVisible && (
                        <Modal animationType="slide" transparent={true} visible={isConfirmModalVisible} onRequestClose={() => setIsConfirmModalVisible(false)}>
                            <HappyAlert
                                visible={isConfirmModalVisible}
                                onConfirm={handleDeleteCart}
                                onCancel={() => setIsConfirmModalVisible(false)}
                                type={"warning"}
                                message={t("delete_cart_confirm")}
                                confirmText={t("yes")}
                                cancelText={t("no")}
                            />
                        </Modal>
                    )}
                    {isPaymentChoseVisible && (
                        <Modal
                            visible={isPaymentChoseVisible}
                            transparent={true}
                            animationType="slide"
                            onRequestClose={() => setIsPaymentChoseVisible(false)}
                        >
                            <TouchableOpacity style={styles.modalOverlay} onPress={() => setIsPaymentChoseVisible(false)}>
                                <View style={[styles.modalContent, isPhoneLayout && { width: '92%' }]}>
                                    <PaymentSelector onSelect={handleOrderSubmission} />
                                </View>
                            </TouchableOpacity>
                        </Modal>
                    )}

                </View>
            </LinearGradient>
        </GestureHandlerRootView>
    );
};

const buildStyles = (theme, isDark) => ({
    container: {
        flex: 1,
        padding: 16,
        paddingTop: 10,
        zIndex: 999
    },
    processButton: {
        backgroundColor: theme.colors.primary,
        alignItems: 'center',
        padding: 7,
        paddingVertical: 15,
        marginTop: 10,
        marginBottom: 10,
        borderRadius: 5
    },
    saveTableButton: {
        backgroundColor: theme.colors.card,
        alignItems: 'center',
        padding: 7,
        paddingVertical: 15,
        marginTop: 10,
        marginBottom: 10,
        borderRadius: 5
    },
    confirmButton: {
        marginTop: 10,
        padding: 10,
        backgroundColor: theme.colors.success,
        borderRadius: 5,
    },
    confirmButtonText: {
        color: 'white',
        fontSize: 16,
        textAlign: 'center',
    },
    processButtonText: {
        fontSize: 20,
        color: '#ececec',
    },
    saveTableText: {
        fontSize: 20,
        color: theme.colors.primary,
    },
    unitPrice: {
        fontSize: 12,
        color: theme.colors.subText,
    },
    modalOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
    },
    modalContent: {
        width: width >= 768 ? '60%' : '75%',
        maxHeight: '80%',
        backgroundColor: theme.colors.card,
        borderRadius: 10,
        padding: 20,
        alignItems: 'center',
        justifyContent: 'center',
    },
    closeButton: {
        marginTop: 10,
        padding: 10,
        backgroundColor: theme.colors.primary,
        borderRadius: 5,
    },
    closeButtonText: {
        color: 'white',
        fontSize: 16,
    },
    itemContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 12,
        paddingHorizontal: 10,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
    },
    selectedItemContainer: {
        backgroundColor: 'rgba(255,245,189,0.67)',
    },
    itemName: {
        fontSize: 16,
        color: theme.colors.text,
    },
    quantityContainer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    quantityText: {
        marginHorizontal: 8,
        fontSize: 16,
        fontWeight: "700",
        color: theme.colors.text,
    },
    // ±1 steppers: 36dp pills (+hitSlop ≈ 44dp touch target, the mobile
    // minimum). They used to be bare 20dp icons that phones kept missing.
    quantityButton: {
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.colors.inputBackground || theme.colors.card,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    quantityValue: {
        minWidth: 32,
        minHeight: 36,
        alignItems: 'center',
        justifyContent: 'center',
    },
    // Phone UI: 48dp steppers + a big tappable quantity (opens the numpad sheet).
    quantityButtonPhone: {
        width: 48,
        height: 48,
        borderRadius: 24,
    },
    quantityValuePhone: {
        minWidth: 48,
        minHeight: 48,
    },
    quantityTextPhone: {
        fontSize: 22,
        fontWeight: '900',
    },
    processButtonPhone: {
        minHeight: 64,
        paddingVertical: 18,
        borderRadius: 18,
        justifyContent: 'center',
    },
    processButtonTextPhone: {
        fontSize: 22,
        fontWeight: '900',
    },
    itemPrice: {
        fontSize: 16,
        color: theme.colors.text,
    },
    totalContainer: {
        padding: 10,
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
        justifyContent: 'space-between',
    },
    detailsContainer: {
        padding: 2,
        justifyContent: 'space-between',
    },
    detailsText: {
        fontSize: 16,
        marginBottom: 5,
        color: theme.colors.text,
    },
    productSumText: {
        fontSize: 18,
        marginBottom: 3,
        color: theme.colors.text,
    },
    brighterBackground: {
        backgroundColor: 'rgba(255,246,207,0.67)', // Brighter background color
    },
    totalText: {
        fontSize: 18,
        fontWeight: 'bold',
        marginBottom: 8,
        color: theme.colors.text,
    },
    animationContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center'
    },
    lottie: {
        width: Dimensions.get("screen").width - 32,
        height: Dimensions.get("screen").width - 32,
    },
    background: {
        flex: 1,
        backgroundColor: theme.colors.background,
        width: '100%',
        height: height - 90
    },
    spinnerTextStyle: {
        color: '#FFF'
    },
    swipeButton: {
        justifyContent: 'center',
        alignItems: 'center',
        width: 80,
        backgroundColor: 'transparent',
    },
    extraRow: {
        flexDirection: 'row',
        padding: 10,
        backgroundColor: theme.colors.card,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
    },
    selectedExtraRow: {
        backgroundColor: 'rgba(255,245,189,0.67)',
    },
    extraButton: {
        flex: 1,
        padding: 14,
        backgroundColor: theme.colors.border,
        borderRadius: 4,
        marginHorizontal: 4,
        alignItems: 'center',
    },
    deleteButton: {
        backgroundColor: theme.colors.error,
        color: '#ddd',
    },
    discountButton: {
        backgroundColor: theme.colors.primary,
        color: '#ddd',
    },
    commentButton: {
        backgroundColor: theme.colors.accent,
        color: '#ddd',
    },
    chevronButton: {
        flex: 0.4,
        padding: 10,
        backgroundColor: theme.colors.inputBackground,
        borderRadius: 4,
        marginHorizontal: 4,
        alignItems: 'center',
    },
    modalView: {
        margin: 15,
        backgroundColor: theme.colors.card,
        borderRadius: 15,
        padding: 35,
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.25,
        shadowRadius: 4,
        elevation: 5,
    },
    textInput: {
        height: 40,
        borderColor: theme.colors.border,
        borderWidth: 1,
        borderRadius: 4,
        marginBottom: 20,
        width: '100%',
        paddingHorizontal: 10,
    },
    scrollView: {
        maxHeight: 200, // Set the maximum height for the scrollable area
    },
    scrollViewContent: {
        paddingBottom: 10,
    },
    discountContainer: {
        padding: 10,
        backgroundColor: theme.colors.card,
        borderRadius: 5,
    },
    discountTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        marginBottom: 5,
    },
    discountText: {
        fontSize: 14,
        color: theme.colors.text,
    },
    itemDiscountContainer: {
        padding: 10,
        backgroundColor: theme.colors.card,
        borderRadius: 5,
    },
    originalSumContainer: {
        padding: 10,
        backgroundColor: theme.colors.card,
        borderRadius: 5,
        marginTop: 10,
    },
    quantityInput: {
        height: 30,
        minWidth: 30,
        maxWidth: 60,
        borderColor: theme.colors.border,
        borderWidth: 1,
        paddingHorizontal: 2,
        borderRadius: 4,
        textAlign: 'center',
        color: theme.colors.text,
    },
    originalSumText: {
        fontSize: 16,
        fontWeight: 'bold',
        color: theme.colors.text,
    },
    tableNameContainer: {
        padding: 6,
        backgroundColor: theme.colors.primary,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
        alignItems: 'center',
        borderRadius: 5,
    },
    tableNameText: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#fff3e9',
    },
    quickReceiptContainer: {
        padding: 6,
        backgroundColor: theme.colors.card,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
        alignItems: 'center',
        borderRadius: 5,
    },
    quickReceiptText: {
        fontSize: 16,
        fontWeight: 'bold',
        color: theme.colors.text,
    },
});

export default CartScreen;
